import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { jobApi, profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { ChipsInput as TagInput, ChoiceChips, sameText } from '@/components/ui/Chips'
import { ErrorState, Skeleton } from '@/components/ui/Feedback'
import { FieldShell } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { JobPreference, Profile, ProfileSearchResult } from '@/types/api'

interface Props {
  open: boolean
  onClose: () => void
  onFound?: (result: ProfileSearchResult) => void
}

const WORKPLACE_OPTIONS = [
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'On-site' },
]

const EMPLOYMENT_OPTIONS = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'internship', label: 'Internship' },
]

const LIMIT_OPTIONS = [25, 50, 100, 200, 500, 1000]
const DEFAULT_LIMIT = 50

interface Draft {
  titles: string[]
  locations: string[]
  years: string
  skills: string[]
  workplaceTypes: string[]
  employmentTypes: string[]
  limit: number
}

function draftFrom(profile: Profile): Draft {
  const preference = profile.jobPreference
  return {
    // With nothing chosen yet, start from the role they hold now.
    titles: preference?.desiredTitles.length
      ? preference.desiredTitles
      : profile.currentTitle
        ? [profile.currentTitle]
        : [],
    locations: preference?.desiredLocations ?? [],
    years: profile.totalExperienceYears == null ? '' : String(profile.totalExperienceYears),
    skills: profile.skills.map((skill) => skill.name),
    workplaceTypes: preference?.workplaceTypes ?? [],
    employmentTypes: preference?.employmentTypes ?? [],
    limit: preference?.matchLimit ?? DEFAULT_LIMIT,
  }
}

/**
 * "Find jobs for me": what the seeker is looking for, and how many jobs to
 * pull. Saving runs a profile search over company career pages; the matches
 * land in the same feed as Naukri's.
 */
export function JobPreferencesModal({ open, onClose, onFound }: Props) {
  const queryClient = useQueryClient()
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: profileApi.get,
    enabled: open,
  })
  const profile = profileQuery.data

  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Start each opening from what is saved, not from the last abandoned edit.
  useEffect(() => {
    if (!open) {
      setDraft(null)
      setError(null)
    } else if (profile) {
      setDraft((current) => current ?? draftFrom(profile))
    }
  }, [open, profile])

  const update = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current))
    setError(null)
  }

  const search = useMutation({
    mutationFn: async () => {
      if (!draft || !profile) throw new Error('Profile not loaded')

      // PUT replaces the whole preference, so carry over what this form does not edit.
      const saved: Partial<JobPreference> = profile.jobPreference ?? {}
      await profileApi.updatePreferences({
        ...saved,
        desiredTitles: draft.titles,
        desiredLocations: draft.locations,
        workplaceTypes: draft.workplaceTypes,
        employmentTypes: draft.employmentTypes,
        matchLimit: draft.limit,
      })

      const years = draft.years.trim() === '' ? null : Number(draft.years)
      if (years !== null && years !== profile.totalExperienceYears) {
        await profileApi.updateIdentity({ totalExperienceYears: years })
      }

      const added = draft.skills.filter(
        (name) => !profile.skills.some((skill) => sameText(skill.name, name)),
      )
      const removed = profile.skills.filter(
        (skill) => !draft.skills.some((name) => sameText(skill.name, name)),
      )
      if (added.length) await profileApi.updateSkills(added.map((name) => ({ name })))
      for (const skill of removed) await profileApi.removeSkill(skill.id)

      return jobApi.profileSearch({ limit: draft.limit })
    },
    onSuccess: (result) => {
      for (const key of ['profile', 'top-matches', 'job-sources', 'recommendations', 'jobs']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      if (result.matched === 0) {
        // Nothing to show on the board: stay here so they can widen the search.
        setError(
          'No openings matched. Try more locations or work modes, or a broader role title.',
        )
        return
      }
      toast.success(
        `Found ${result.matched} job${result.matched === 1 ? '' : 's'} for you`,
        `The best of ${result.eligible.toLocaleString()} relevant openings on company career pages.`,
      )
      onFound?.(result)
      onClose()
    },
    onError: (caught: ApiError | Error) => {
      // The preferences may have saved before the search failed.
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      setError(caught.message)
    },
  })

  const yearsValue = draft?.years.trim() === '' ? null : Number(draft?.years)
  const yearsInvalid = yearsValue !== null && !(yearsValue >= 0 && yearsValue <= 60)
  const nothingToSearch = !!draft && draft.titles.length === 0 && draft.skills.length === 0
  const limits = draft && !LIMIT_OPTIONS.includes(draft.limit)
    ? [...LIMIT_OPTIONS, draft.limit].sort((a, b) => a - b)
    : LIMIT_OPTIONS

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Find jobs for me"
      description="Tell us what you want. We search company career pages for the best matches and add them to your board, next to your Naukri jobs."
      size="lg"
      dismissible={!search.isPending}
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="min-w-0 flex-1 text-[13px] text-ink-muted">
            {search.isPending
              ? 'Searching company career pages. This can take up to a minute.'
              : 'Nothing is applied to until you choose Apply.'}
          </p>
          <Button
            className="w-full sm:w-auto"
            onClick={() => search.mutate()}
            loading={search.isPending}
            disabled={!draft || nothingToSearch || yearsInvalid}
          >
            Find <span className="num">{draft?.limit ?? DEFAULT_LIMIT}</span> jobs →
          </Button>
        </div>
      }
    >
      {profileQuery.isError ? (
        <ErrorState
          description="We could not load your profile."
          onRetry={() => profileQuery.refetch()}
        />
      ) : !draft ? (
        <div className="space-y-4 pb-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-field" />
          ))}
        </div>
      ) : (
        <div className="space-y-5 pb-4">
          <TagInput
            label="Roles you are looking for"
            values={draft.titles}
            onChange={(values) => update('titles', values)}
            placeholder="e.g. Engineering Manager"
            hint="Job titles. Seniority words are optional: “Software Engineer” also finds Senior, Lead and Staff roles that fit your experience."
          />

          <TagInput
            label="Locations"
            values={draft.locations}
            onChange={(values) => update('locations', values)}
            placeholder={profile?.location?.city ? `e.g. ${profile.location.city}` : 'e.g. Bengaluru'}
            hint={
              draft.locations.length
                ? 'Only jobs in these places. Select Remote under Work mode to also include remote jobs based elsewhere.'
                : 'Leave empty to search the whole country you can work in; jobs near you rank higher.'
            }
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <FieldShell
              label="Years of experience"
              htmlFor="pref-years"
              error={yearsInvalid ? 'Enter a number between 0 and 60.' : null}
              hint="Roles asking for much more, or capped well below, are left out."
            >
              <input
                id="pref-years"
                type="number"
                inputMode="decimal"
                min={0}
                max={60}
                step={0.5}
                value={draft.years}
                onChange={(event) => update('years', event.target.value)}
                className="field-box num h-11 w-full"
              />
            </FieldShell>

            <FieldShell label="How many jobs" hint="The best matches, spread across employers.">
              <div className="flex gap-1.5" role="radiogroup" aria-label="How many jobs">
                {limits.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={draft.limit === value}
                    onClick={() => update('limit', value)}
                    className={cn(
                      'num h-11 min-w-0 flex-1 rounded-field border text-[14px] font-medium transition-colors',
                      draft.limit === value
                        ? 'border-ink bg-ink text-oncolor'
                        : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
                    )}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </FieldShell>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <ChoiceChips
              label="Work mode"
              options={WORKPLACE_OPTIONS}
              values={draft.workplaceTypes}
              onChange={(values) => update('workplaceTypes', values)}
              hint="None selected means any."
            />
            <ChoiceChips
              label="Job type"
              options={EMPLOYMENT_OPTIONS}
              values={draft.employmentTypes}
              onChange={(values) => update('employmentTypes', values)}
              hint="None selected means any."
            />
          </div>

          <TagInput
            label="Skills"
            values={draft.skills}
            onChange={(values) => update('skills', values)}
            placeholder="Add a skill"
            hint="From your profile. Jobs are ranked by how many of these they ask for."
          />

          {nothingToSearch && (
            <p className="text-[13px] text-ink-muted">
              Add at least one role or skill so we know what to look for.
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="rounded-field border border-danger/20 bg-danger/[0.06] px-3.5 py-2.5 text-[13px] text-danger"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
